import { type Ref } from "vue";
import { useComplexModelModelWithChanges } from "@/features/core-shared/composables/useComplexModelWithChanges";
import {
  type AddressFieldValues,
  type LocationType,
  areValidCoordinates,
  getEmptyAddress,
} from "@/features/experience-shared/utils/address-utils";

export interface RawLocationData {
  latitude?: string;
  longitude?: string;
  address?: {
    direction?: string;
    postal_code?: string;
  };
}

/**
 * Factory for the address field.
 *
 * @param locationData - reactive ref of the raw location data from the data source
 */
export function useAddressField(locationData: Ref<RawLocationData | undefined>) {
  const initialAddress = computed<AddressFieldValues>(() => {
    const loc = locationData.value;
    if (!loc) return getEmptyAddress();

    const lat = Number.parseFloat(loc.latitude || "");
    const lng = Number.parseFloat(loc.longitude || "");
    const hasValidCoords = areValidCoordinates(lat, lng);

    return {
      direction: loc.address?.direction || "",
      latitude: hasValidCoords ? lat : undefined,
      longitude: hasValidCoords ? lng : undefined,
      postalCode: loc.address?.postal_code || "",
    };
  });

  const initialLocationType = computed<LocationType>(() => {
    const addr = initialAddress.value;
    return areValidCoordinates(addr.latitude, addr.longitude) && !addr.direction ? "COORDINATES" : "ADDRESS";
  });

  const {
    model: address,
    hasChanges: addressHasChanges,
    isFilled,
    reset: resetAddress,
  } = useComplexModelModelWithChanges<AddressFieldValues>(initialAddress);

  const locationType = ref<LocationType>(initialLocationType.value);

  watch(initialLocationType, (newVal, oldVal) => {
    if (locationType.value === oldVal) {
      locationType.value = newVal;
    }
  });

  const hasChanges = computed(() => addressHasChanges.value || locationType.value !== initialLocationType.value);
  const addressHasCoordinates = computed(() => areValidCoordinates(address.value.latitude, address.value.longitude));

  function resetAddressField() {
    address.value = getEmptyAddress();
  }

  function reset() {
    resetAddress();
    locationType.value = initialLocationType.value;
  }

  return {
    id: "location.address" as const,
    address,
    locationType,
    addressHasCoordinates,
    hasChanges,
    isFilled,
    resetAddressField,
    reset,
  };
}
